//schedulingemployee.js
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/Trail loading.json";
import { useState, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { db } from "../firebase/config";
import { useAuth } from "../Context/AuthContext";

import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import "../styles/SchedulingEmployee.css";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

function Loader({ label = "Loading schedule..." }) {
  return (
    <div className="loader-inline">
      <Lottie
        animationData={loadingAnimation}
        loop
        style={{ width: 140, height: 140 }}
      />
      <div className="loader-label">{label}</div>
    </div>
  );
}

export default function SchedulingEmployee() {
  const [schedules, setSchedules] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [error, setError] = useState("");
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [typeMap, setTypeMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useAuth();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);

      if (!user?.uid) {
        setError("User not authenticated.");
        setIsLoading(false);
        return;
      }

      const employeeId = user.uid;
      const companyId = user.companyId;

      if (!companyId) {
        setError("No company linked to this account.");
        setIsLoading(false);
        return;
      }

      try {
        // company TZ
        const companySnapshot = await getDoc(doc(db, "companies", companyId));
        const tz = companySnapshot.data()?.timeZone || "UTC";
        setCompanyTimeZone(tz);

        // activity types
        const typesSnap = await getDocs(
          collection(db, `companies/${companyId}/activity_types`),
        );
        const tmap = {};
        typesSnap.forEach((d) => {
          tmap[d.id] = { id: d.id, ...d.data() };
        });
        setTypeMap(tmap);

        // schedules
        const qRef = query(
          collection(db, `companies/${companyId}/schedules`),
          where("employeeId", "==", employeeId),
        );
        const qSnap = await getDocs(qRef);

        const all = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        all.sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));
        setSchedules(all);

        // select week
        const todayISO = DateTime.now()
          .setZone(tz)
          .toISODate();
        const current =
          all.find(
            (s) => s.weekStartDate <= todayISO && todayISO <= s.weekEndDate,
          ) ||
          all.find((s) => s.weekStartDate >= todayISO) ||
          all[all.length - 1] ||
          null;

        setSelectedWeekStart(current ? current.weekStartDate : null);
        setError("");
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load schedule.");
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [user]);

  const iso = (d) => DateTime.fromISO(d, { zone: companyTimeZone });

  function materializeRepeatingWeek(baseSchedule, targetWeekStartISO) {
    const baseStart = iso(baseSchedule.weekStartDate);
    const targetStart = iso(targetWeekStartISO);
    const deltaDays = Math.round(targetStart.diff(baseStart, "days").days);

    const shifts = (baseSchedule.shifts || []).map((s) => {
      const newDate = iso(s.date)
        .plus({ days: deltaDays })
        .toISODate();
      return { ...s, date: newDate };
    });

    return {
      ...baseSchedule,
      weekStartDate: targetWeekStartISO,
      weekEndDate: targetStart.plus({ days: 6 }).toISODate(),
      shifts,
      _fromRecurring: baseSchedule.weekStartDate,
    };
  }

  function scheduleForWeek(weekStartISO) {
    if (!weekStartISO) return null;

    const exact = schedules.find((s) => s.weekStartDate === weekStartISO);
    if (exact) return exact;

    const prior = [...schedules]
      .filter((s) => s.weekStartDate <= weekStartISO)
      .sort((a, b) => b.weekStartDate.localeCompare(a.weekStartDate))[0];

    if (!prior) return null;
    if (prior.repeatWeekly)
      return materializeRepeatingWeek(prior, weekStartISO);
    return null;
  }
  // "17:00" -> "5:00 PM" (company TZ safe)
  const formatTime = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return "";
    const dt = DateTime.fromFormat(hhmm, "HH:mm", { zone: companyTimeZone });
    return dt.isValid ? dt.toFormat("h:mm a") : hhmm; // fallback if invalid
  };

  const formatRange = (start, end) => {
    const s = formatTime(start);
    const e = formatTime(end);
    if (!s || !e) return "";
    return `${s} – ${e}`;
  };

  const fallbackByLabel = (lbl = "") => {
    const k = lbl.toLowerCase();
    if (k === "break") return "#7a869a";
    if (k === "meeting") return "#3d8bfd";
    if (k === "training") return "#22b573";
    if (k === "other") return "#8f62ff";
    return "#8f62ff";
  };

  const colorForActivity = (a) => {
    const byId = a.typeId && typeMap[a.typeId]?.color;
    if (byId) return byId;
    const byLabel = Object.values(typeMap).find(
      (t) => t.label?.toLowerCase() === (a.type || "").toLowerCase(),
    )?.color;
    return byLabel || fallbackByLabel(a.type);
  };

  const toMin = (hhmm = "00:00") => {
    const [h, m] = (hhmm || "00:00").split(":").map(Number);
    return h * 60 + m;
  };

  const segmentsForInterval = (start, end) => {
    const s = toMin(start);
    const e = toMin(end);

    // normal (same-day)
    if (e > s) {
      return [{ leftPct: (s / 1440) * 100, widthPct: ((e - s) / 1440) * 100 }];
    }

    // overnight: split into [start→24:00] and [00:00→end]
    return [
      { leftPct: (s / 1440) * 100, widthPct: ((1440 - s) / 1440) * 100 },
      { leftPct: 0, widthPct: (e / 1440) * 100 },
    ];
  };

  const wk = selectedWeekStart ? scheduleForWeek(selectedWeekStart) : null;

  return (
    <div className="employeeSchedule-wrapper">
      <nav className="employeeschedule-nav">
        <p className="employeeschedule-breadcrumb">
          <Link to="/employee-dashboard">Dashboard</Link> /{" "}
          <span>Schedule</span>
        </p>
      </nav>

      <div className="employeeSchedule-container">
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <div className="employeeSchedule-header">
              <h2>Weekly schedule</h2>

              {wk && (
                <p>
                  Weekly : {wk.weekStartDate} – {wk.weekEndDate}
                  {wk._fromRecurring && (
                    <em className="recurring-note">
                      (recurring from {wk._fromRecurring})
                    </em>
                  )}
                </p>
              )}
            </div>

            {selectedWeekStart && (
              <div className="week-nav">
                <div className="week-nav__left">
                  <button
                    type="button"
                    className="wkbtn wkbtn--ghost"
                    onClick={() =>
                      setSelectedWeekStart(
                        iso(selectedWeekStart)
                          .minus({ days: 7 })
                          .toISODate(),
                      )
                    }
                    aria-label="Previous week"
                    title="Previous week"
                  >
                    <FiChevronLeft size={18} />
                    <span>Prev</span>
                  </button>
                </div>

                <div className="week-nav__center">
                  <span className="week-chip">
                    {iso(selectedWeekStart).toFormat("d LLL yyyy")} –{" "}
                    {iso(selectedWeekStart)
                      .plus({ days: 6 })
                      .toFormat("d LLL yyyy")}
                  </span>
                </div>

                <div className="week-nav__right">
                  <button
                    type="button"
                    className="wkbtn wkbtn--ghost"
                    onClick={() =>
                      setSelectedWeekStart(
                        iso(selectedWeekStart)
                          .plus({ days: 7 })
                          .toISODate(),
                      )
                    }
                    aria-label="Next week"
                    title="Next week"
                  >
                    <span>Next</span>
                    <FiChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {Object.keys(typeMap).length > 0 && (
              <div className="legend" aria-hidden="true">
                {Object.entries(typeMap).map(([id, t]) => (
                  <span key={id} className="legend-item">
                    <span
                      className="legend-swatch"
                      style={{ background: t.color || "#d1d5db" }}
                    />
                    {t.label}
                  </span>
                ))}
              </div>
            )}

            {error && <p className="employeeSchedule-error">{error}</p>}

            {!wk ? (
              <p className="employeeSchedule-empty">
                No assigned schedule for this week.
              </p>
            ) : (
              <table className="employeeSchedule-table">
                <tbody>
                  {[...wk.shifts]
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(
                      ({
                        date,
                        startTime,
                        stopTime,
                        isOffDay,
                        isHoliday,
                        isCallOut, // ✅ add
                        locationName,
                        activities = [],
                      }) => {
                        const formattedDate = DateTime.fromISO(date, {
                          zone: companyTimeZone,
                        }).toLocaleString({
                          weekday: "long",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        });

                        const statusText = isCallOut
                          ? "Call-out"
                          : isOffDay
                          ? "Off Day"
                          : isHoliday
                          ? "Holiday"
                          : formatRange(startTime, stopTime);

                        return (
                          <Fragment key={date}>
                            <tr className="day-row">
                              <td className="day-left">{formattedDate}</td>
                              <td className="day-right">
                                <div className="day-right-stack">
                                  <strong className="day-time">
                                    {statusText}
                                  </strong>

                                  {!isOffDay && !isHoliday && (
                                    <div className="day-worksite">
                                      {locationName || "Main Office"}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {!isOffDay && !isHoliday && !isCallOut && (
                              <tr className="timeline-row">
                                <td colSpan={2}>
                                  <div className="timeline">
                                    {segmentsForInterval(
                                      startTime,
                                      stopTime,
                                    ).map((seg, segIdx) => (
                                      <div
                                        key={`shift-${date}-${segIdx}`}
                                        className="bar shift"
                                        style={{
                                          left: `${seg.leftPct}%`,
                                          width: `${seg.widthPct}%`,
                                        }}
                                        title={formatRange(startTime, stopTime)}
                                      >
                                        <span className="bar-label">Shift</span>
                                      </div>
                                    ))}

                                    {activities.flatMap((a, i) =>
                                      segmentsForInterval(a.start, a.end).map(
                                        (seg, segIdx) => (
                                          <div
                                            key={`${date}-${i}-${segIdx}`}
                                            className={`bar activity ${(
                                              a.type || "other"
                                            ).toLowerCase()}`}
                                            style={{
                                              left: `${seg.leftPct}%`,
                                              width: `${seg.widthPct}%`,
                                              background: colorForActivity(a),
                                            }}
                                            title={`${a.type ||
                                              "Activity"} ${formatRange(
                                              a.start,
                                              a.end,
                                            )}${a.paid ? " (paid)" : ""}${
                                              a.note ? ` • ${a.note}` : ""
                                            }`}
                                          >
                                            <span className="bar-label">
                                              {(a.type || "").toLowerCase() ===
                                              "break"
                                                ? "Brk"
                                                : a.type || "Act"}
                                            </span>
                                          </div>
                                        ),
                                      ),
                                    )}
                                  </div>

                                  <div className="time-axis" aria-hidden="true">
                                    <span>00</span>
                                    <span>06</span>
                                    <span>12</span>
                                    <span>18</span>
                                    <span>24</span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      },
                    )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
